import React, { useCallback, useMemo, useRef, useState } from 'react'
import {
  FolderOpenIcon,
  CheckCircleIcon,
  ExclamationTriangleIcon,
  ArrowPathIcon,
  XMarkIcon,
} from '@heroicons/react/24/outline'
import {
  Button,
  Input,
  Text,
  LoadingSpinner,
  Select,
  SortableTableHeader,
} from '../../components/ui'
import { useUserLogs } from '../../hooks/useUserLogs'
import { useAuthContext } from '../../contexts/AuthContext'
import { financeiroService } from '../../services/financeiro'
import {
  extractYearFromFolderName,
  formatMoneyBr,
  isShowDirectoryPickerAvailable,
  listingFromWebkitFiles,
  parseComissaoFile,
  pickComissaoRootFolder,
  type FinanceiroRootPickerSession,
} from '../../utils/financeiroComissoesImport'
import {
  FinanceiroPastaJaProcessadaError,
  type FinanceiroComissaoFileProgress,
  type FinanceiroComissaoRow,
  type FinanceiroComissaoSaveFolderProgress,
  type FinanceiroComissaoSubpasta,
} from '../../types/financeiro'

type ProcessPhase = 'idle' | 'lendo' | 'gravando'

const PAGE_SIZE = 20
const ALL_FOLDERS_TAB = '__todas__'

type SortKey = keyof FinanceiroComissaoRow

const COLUMN_DEFS: Array<{ key: SortKey; label: string }> = [
  { key: 'pasta', label: 'Pasta' },
  { key: 'arquivo', label: 'Arquivo' },
  { key: 'codigoCorretor', label: 'Código do Corretor' },
  { key: 'nomeCorretor', label: 'Nome do Corretor' },
  { key: 'cpfCnpj', label: 'CPF/CNPJ' },
  { key: 'apolice', label: 'Apólice' },
  { key: 'numeroSub', label: 'Número Sub' },
  { key: 'modulo', label: 'Módulo' },
  { key: 'estipulante', label: 'Estipulante' },
  { key: 'subEstipulante', label: 'Sub Estipulante' },
  { key: 'fatura', label: 'Fatura' },
  { key: 'competencia', label: 'Competência' },
  { key: 'valorApropriado', label: 'Valor Apropriado' },
  { key: 'tipoComissao', label: 'Tipo de Comissão' },
  { key: 'parcelaInicial', label: 'Parcela Inicial' },
  { key: 'numeroGrupoCobertura', label: 'Nº Grupo Cobertura' },
  { key: 'valorBase', label: 'Valor Base' },
  { key: 'percentualComissao', label: '% Comissão' },
  { key: 'valorComissao', label: 'Valor da Comissão' },
  { key: 'dataEmissao', label: 'Data de Emissão' },
  { key: 'dataApropriacao', label: 'Data da Apropriação' },
  { key: 'valorPremioBruto', label: 'Prêmio Bruto' },
  { key: 'valorPremioTarifario', label: 'Prêmio Tarifário' },
  { key: 'dataMovimento', label: 'Data do Movimento' },
  { key: 'dataAgendamento', label: 'Data do Agendamento' },
  { key: 'dataPagamento', label: 'Data do Pagamento' },
  { key: 'dataPagamentoEfetivo', label: 'Data Pagamento Efetivo' },
  { key: 'statusRecibo', label: 'Status Recibo' },
  { key: 'nrCobranca', label: 'Nr Cobrança' },
]

const MONEY_KEYS = new Set<SortKey>([
  'valorApropriado',
  'valorBase',
  'percentualComissao',
  'valorComissao',
  'valorPremioBruto',
  'valorPremioTarifario',
])

function subTabClass(active: boolean): string {
  return `py-3 px-1 border-b-2 font-medium text-sm whitespace-nowrap ${
    active
      ? 'border-primary-600 text-primary-600 dark:text-primary-400'
      : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300 dark:text-gray-400 dark:hover:text-gray-200'
  }`
}

function compareValues(a: unknown, b: unknown, order: 'asc' | 'desc'): number {
  const mul = order === 'asc' ? 1 : -1
  if (a === null || a === undefined || a === '') {
    if (b === null || b === undefined || b === '') return 0
    return 1
  }
  if (b === null || b === undefined || b === '') return -1
  if (typeof a === 'number' && typeof b === 'number') return (a - b) * mul
  return String(a).localeCompare(String(b), 'pt-BR', { numeric: true }) * mul
}

const ComissoesTab: React.FC = () => {
  const { logAction } = useUserLogs()
  const { user } = useAuthContext()
  const folderInputRef = useRef<HTMLInputElement>(null)

  const [pastas, setPastas] = useState<FinanceiroComissaoSubpasta[]>([])
  const [error, setError] = useState<string | null>(null)
  const [isListingRoot, setIsListingRoot] = useState(false)
  const [isLoadingSubfolders, setIsLoadingSubfolders] = useState(false)
  const [pastasProcessadasNomes, setPastasProcessadasNomes] = useState<Set<string>>(
    () => new Set()
  )

  /** Sessão após escolher a pasta raiz (nomes + anos; arquivos ainda não carregados). */
  const [rootSession, setRootSession] = useState<FinanceiroRootPickerSession | null>(null)
  const [selectedYear, setSelectedYear] = useState<string>('')
  const [selectedSubfolderNames, setSelectedSubfolderNames] = useState<string[]>([])

  const [isProcessing, setIsProcessing] = useState(false)
  const [processPhase, setProcessPhase] = useState<ProcessPhase>('idle')
  const [fileProgress, setFileProgress] = useState<FinanceiroComissaoFileProgress[]>([])
  const [saveProgress, setSaveProgress] = useState<FinanceiroComissaoSaveFolderProgress[]>([])
  const [currentFileLabel, setCurrentFileLabel] = useState<string | null>(null)
  const [rows, setRows] = useState<FinanceiroComissaoRow[]>([])
  const [processedFolders, setProcessedFolders] = useState<string[]>([])

  const [activeFolderTab, setActiveFolderTab] = useState<string>(ALL_FOLDERS_TAB)
  const [search, setSearch] = useState('')
  const [sortBy, setSortBy] = useState<SortKey>('nomeCorretor')
  const [sortOrder, setSortOrder] = useState<'asc' | 'desc'>('asc')
  const [currentPage, setCurrentPage] = useState(1)

  const refreshPastasProcessadas = useCallback(async () => {
    try {
      const list = await financeiroService.listPastasProcessadas()
      setPastasProcessadasNomes(new Set(list.map((p) => p.folder_name)))
    } catch (err) {
      console.error(err)
    }
  }, [])

  React.useEffect(() => {
    void refreshPastasProcessadas()
  }, [refreshPastasProcessadas])

  const subfoldersForYear = useMemo(() => {
    if (!rootSession || !selectedYear) return []
    return rootSession.subfolders.filter((s) => s.year === selectedYear)
  }, [rootSession, selectedYear])

  const selectableSubfoldersForYear = useMemo(
    () => subfoldersForYear.filter((s) => !pastasProcessadasNomes.has(s.name)),
    [subfoldersForYear, pastasProcessadasNomes]
  )

  const areAllYearSubfoldersSelected =
    selectableSubfoldersForYear.length > 0 &&
    selectableSubfoldersForYear.every((s) => selectedSubfolderNames.includes(s.name))

  const applyRootSession = useCallback((session: FinanceiroRootPickerSession) => {
    // Exige pasta raiz com subpastas nomeadas por ano — rejeita seleção de subpasta isolada
    if (session.subfolders.length === 0 || session.years.length === 0) {
      setRootSession(null)
      setSelectedYear('')
      setSelectedSubfolderNames([])
      setError(
        'Selecione a pasta raiz (não uma subpasta). Ela deve conter subpastas cujo nome comece com o ano (ex.: 2024_Janeiro).'
      )
      return
    }

    setError(null)
    setRootSession(session)
    setSelectedSubfolderNames([])
    setSelectedYear(session.years[0] ?? '')
  }, [])

  const handleSelectRootClick = async () => {
    if (isProcessing || isListingRoot || isLoadingSubfolders) return
    setError(null)

    // Sempre pasta raiz via File System Access API quando disponível (sem fallback que permita “atalho” de subpasta)
    if (isShowDirectoryPickerAvailable()) {
      try {
        setIsListingRoot(true)
        const session = await pickComissaoRootFolder()
        setIsListingRoot(false)
        if (!session) return
        applyRootSession(session)
      } catch (err) {
        setIsListingRoot(false)
        setError(err instanceof Error ? err.message : 'Não foi possível abrir a pasta raiz.')
      }
      return
    }

    // Apenas em navegadores sem showDirectoryPicker
    folderInputRef.current?.click()
  }

  const handleFolderChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const fileArray = Array.from(e.target.files ?? [])
    e.target.value = ''
    const session = listingFromWebkitFiles(fileArray)
    if (!session) {
      setError('Nenhuma subpasta encontrada na pasta selecionada.')
      return
    }
    applyRootSession(session)
  }

  const onChangeYear = (year: string) => {
    setSelectedYear(year)
    setSelectedSubfolderNames([])
  }

  const onToggleAllYearSubfolders = () => {
    if (areAllYearSubfoldersSelected) {
      setSelectedSubfolderNames((prev) =>
        prev.filter((n) => !selectableSubfoldersForYear.some((s) => s.name === n))
      )
    } else {
      setSelectedSubfolderNames((prev) => {
        const next = new Set(prev)
        for (const s of selectableSubfoldersForYear) next.add(s.name)
        return Array.from(next)
      })
    }
  }

  const onToggleSubfolder = (name: string, checked: boolean) => {
    if (pastasProcessadasNomes.has(name)) return
    if (checked) {
      setSelectedSubfolderNames((prev) => (prev.includes(name) ? prev : [...prev, name]))
    } else {
      setSelectedSubfolderNames((prev) => prev.filter((n) => n !== name))
    }
  }

  const handleAddSelectedSubfolders = async () => {
    if (!rootSession || selectedSubfolderNames.length === 0 || isLoadingSubfolders) return
    const toLoad = selectedSubfolderNames.filter((n) => !pastasProcessadasNomes.has(n))
    if (toLoad.length === 0) {
      setError('Todas as pastas selecionadas já foram processadas.')
      return
    }
    setIsLoadingSubfolders(true)
    setError(null)
    try {
      const loaded = await rootSession.loadSubfolders(toLoad)
      setPastas((prev) => {
        const names = new Set(loaded.map((l) => l.name))
        const kept = prev.filter((p) => !names.has(p.name))
        return [...kept, ...loaded].sort((a, b) => a.name.localeCompare(b.name, 'pt-BR'))
      })
      setRows([])
      setFileProgress([])
      setProcessedFolders([])
      setActiveFolderTab(ALL_FOLDERS_TAB)
      setCurrentPage(1)

      const empty = loaded.filter((l) => l.fileCount === 0)
      const skipped = selectedSubfolderNames.length - toLoad.length
      const msgs: string[] = []
      if (empty.length > 0) {
        msgs.push(`Sem Excel/CSV: ${empty.map((e) => e.name).join(', ')}`)
      }
      if (skipped > 0) {
        msgs.push(`${skipped} pasta(s) já processada(s) foram ignorada(s).`)
      }
      if (msgs.length > 0) setError(msgs.join(' '))

      setSelectedSubfolderNames([])
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Erro ao carregar as pastas selecionadas.')
    } finally {
      setIsLoadingSubfolders(false)
    }
  }

  const cancelRootSession = () => {
    setRootSession(null)
    setSelectedYear('')
    setSelectedSubfolderNames([])
  }

  const removePasta = (name: string) => {
    setPastas((prev) => prev.filter((p) => p.name !== name))
  }

  const clearPastas = () => {
    setPastas([])
    setError(null)
    setRows([])
    setFileProgress([])
    setProcessedFolders([])
  }

  const totalFileCount = useMemo(
    () => pastas.reduce((acc, p) => acc + p.fileCount, 0),
    [pastas]
  )

  const handleProcess = useCallback(async () => {
    if (isProcessing || pastas.length === 0) return

    const toProcess = pastas.filter((p) => !pastasProcessadasNomes.has(p.name))
    const skippedAlready = pastas.length - toProcess.length

    if (toProcess.length === 0) {
      setError('Todas as pastas da lista já foram processadas no banco.')
      return
    }

    const filesFlat: Array<{ pasta: string; file: File }> = []
    for (const folder of toProcess) {
      for (const file of folder.files) {
        filesFlat.push({ pasta: folder.name, file })
      }
    }

    if (filesFlat.length === 0) {
      setError('Nenhum arquivo elegível nas pastas a processar.')
      return
    }

    setIsProcessing(true)
    setProcessPhase('lendo')
    setError(null)
    setRows([])
    setSaveProgress([])
    setCurrentPage(1)
    setActiveFolderTab(ALL_FOLDERS_TAB)

    const initialProgress: FinanceiroComissaoFileProgress[] = filesFlat.map(({ pasta, file }, idx) => ({
      id: `${pasta}::${file.name}::${idx}`,
      pasta,
      fileName: file.name,
      relativePath: (file as File & { webkitRelativePath?: string }).webkitRelativePath || file.name,
      status: 'pendente',
      rowCount: 0,
    }))
    setFileProgress(initialProgress)

    const collected: FinanceiroComissaoRow[] = []
    const rowsByPasta = new Map<string, FinanceiroComissaoRow[]>()
    const fileStatsByPasta = new Map<string, { ok: number; erro: number }>()
    let okCount = 0
    let errCount = 0
    let totalRows = 0
    let savedPastas = 0
    let savedLinhas = 0
    const saveWarnings: string[] = []

    for (let i = 0; i < filesFlat.length; i++) {
      const { pasta, file } = filesFlat[i]
      const id = initialProgress[i].id
      setCurrentFileLabel(`${pasta}/${file.name}`)
      setFileProgress((prev) =>
        prev.map((p) => (p.id === id ? { ...p, status: 'lendo' } : p))
      )

      await new Promise((r) => setTimeout(r, 0))

      const result = await parseComissaoFile(file, pasta)
      const stats = fileStatsByPasta.get(pasta) ?? { ok: 0, erro: 0 }
      if (result.ok) {
        collected.push(...result.rows)
        const bucket = rowsByPasta.get(pasta) ?? []
        bucket.push(...result.rows)
        rowsByPasta.set(pasta, bucket)
        totalRows += result.rows.length
        okCount += 1
        stats.ok += 1
        setFileProgress((prev) =>
          prev.map((p) =>
            p.id === id ? { ...p, status: 'ok', rowCount: result.rows.length } : p
          )
        )
      } else {
        errCount += 1
        stats.erro += 1
        setFileProgress((prev) =>
          prev.map((p) =>
            p.id === id ? { ...p, status: 'erro', error: result.message, rowCount: 0 } : p
          )
        )
      }
      fileStatsByPasta.set(pasta, stats)
    }

    setRows(collected)
    setProcessedFolders(toProcess.map((t) => t.name))

    setProcessPhase('gravando')
    const initialSave: FinanceiroComissaoSaveFolderProgress[] = toProcess.map((folder) => ({
      pasta: folder.name,
      status: 'pendente',
      totalRows: (rowsByPasta.get(folder.name) ?? []).length,
      savedRows: 0,
    }))
    setSaveProgress(initialSave)

    for (const folder of toProcess) {
      const pastaRows = rowsByPasta.get(folder.name) ?? []
      const stats = fileStatsByPasta.get(folder.name) ?? { ok: 0, erro: 0 }
      setSaveProgress((prev) =>
        prev.map((p) =>
          p.pasta === folder.name ? { ...p, status: 'gravando', savedRows: 0 } : p
        )
      )
      setCurrentFileLabel(
        pastaRows.length === 0
          ? `Gravando ${folder.name} (sem linhas)...`
          : `Gravando ${folder.name}: 0/${pastaRows.length}`
      )
      try {
        await financeiroService.saveComissoesIcatu({
          pastaNome: folder.name,
          ano: extractYearFromFolderName(folder.name),
          rootFolderName: rootSession?.rootName ?? null,
          rows: pastaRows,
          arquivosOk: stats.ok,
          arquivosErro: stats.erro,
          userId: user?.id ?? null,
          onProgress: ({ savedRows, totalRows }) => {
            setSaveProgress((prev) =>
              prev.map((p) =>
                p.pasta === folder.name
                  ? { ...p, status: 'gravando', savedRows, totalRows }
                  : p
              )
            )
            setCurrentFileLabel(`Gravando ${folder.name}: ${savedRows}/${totalRows}`)
          },
        })
        savedPastas += 1
        savedLinhas += pastaRows.length
        setSaveProgress((prev) =>
          prev.map((p) =>
            p.pasta === folder.name
              ? { ...p, status: 'ok', savedRows: p.totalRows }
              : p
          )
        )
      } catch (err) {
        if (err instanceof FinanceiroPastaJaProcessadaError) {
          saveWarnings.push(`"${folder.name}" já processada`)
          setSaveProgress((prev) =>
            prev.map((p) =>
              p.pasta === folder.name
                ? { ...p, status: 'ja_processada', error: 'Já processada no banco' }
                : p
            )
          )
        } else {
          const message = err instanceof Error ? err.message : 'erro ao gravar'
          saveWarnings.push(`"${folder.name}": ${message}`)
          setSaveProgress((prev) =>
            prev.map((p) =>
              p.pasta === folder.name ? { ...p, status: 'erro', error: message } : p
            )
          )
        }
      }
    }

    await refreshPastasProcessadas()
    setCurrentFileLabel(null)
    setProcessPhase('idle')
    setIsProcessing(false)

    const avisoParts: string[] = []
    if (skippedAlready > 0) {
      avisoParts.push(`${skippedAlready} pasta(s) já processada(s) ignorada(s)`)
    }
    if (saveWarnings.length > 0) {
      avisoParts.push(saveWarnings.join('; '))
    }
    if (avisoParts.length > 0) setError(avisoParts.join(' — '))

    await logAction(
      'import',
      'other',
      null,
      `Extrato Comissões Icatu: ${savedPastas} pasta(s) gravada(s), ${okCount} arquivo(s) ok, ${errCount} erro(s), ${savedLinhas} linha(s)`,
      {
        pastasGravadas: savedPastas,
        arquivosOk: okCount,
        arquivosErro: errCount,
        linhas: savedLinhas,
        ignoradasJaProcessadas: skippedAlready,
      }
    )
  }, [
    isProcessing,
    pastas,
    pastasProcessadasNomes,
    logAction,
    rootSession?.rootName,
    user?.id,
    refreshPastasProcessadas,
  ])

  const toggleSort = (key: string) => {
    const k = key as SortKey
    if (sortBy === k) {
      setSortOrder((o) => (o === 'asc' ? 'desc' : 'asc'))
    } else {
      setSortBy(k)
      setSortOrder('asc')
    }
    setCurrentPage(1)
  }

  const filteredRows = useMemo(() => {
    let list = rows
    if (activeFolderTab !== ALL_FOLDERS_TAB) {
      list = list.filter((r) => r.pasta === activeFolderTab)
    }
    const q = search.trim().toLowerCase()
    if (q) {
      list = list.filter(
        (r) =>
          r.nomeCorretor.toLowerCase().includes(q) ||
          r.codigoCorretor.toLowerCase().includes(q) ||
          r.apolice.toLowerCase().includes(q) ||
          r.estipulante.toLowerCase().includes(q) ||
          r.arquivo.toLowerCase().includes(q) ||
          r.subEstipulante.toLowerCase().includes(q)
      )
    }
    return list
  }, [rows, activeFolderTab, search])

  const sortedRows = useMemo(() => {
    const copy = [...filteredRows]
    copy.sort((a, b) => compareValues(a[sortBy], b[sortBy], sortOrder))
    return copy
  }, [filteredRows, sortBy, sortOrder])

  const totalPages = Math.max(1, Math.ceil(sortedRows.length / PAGE_SIZE))
  const startIndex = (currentPage - 1) * PAGE_SIZE
  const endIndex = Math.min(currentPage * PAGE_SIZE, sortedRows.length)
  const pageRows = sortedRows.slice(startIndex, endIndex)

  React.useEffect(() => {
    if (currentPage > totalPages) setCurrentPage(totalPages)
  }, [currentPage, totalPages])

  const doneCount = fileProgress.filter((p) => p.status === 'ok' || p.status === 'erro').length
  const readPct =
    fileProgress.length === 0 ? 0 : Math.round((doneCount / fileProgress.length) * 100)

  const saveDoneFolders = saveProgress.filter(
    (p) => p.status === 'ok' || p.status === 'erro' || p.status === 'ja_processada'
  ).length
  const saveRowsTotal = saveProgress.reduce((acc, p) => acc + p.totalRows, 0)
  const saveRowsDone = saveProgress.reduce((acc, p) => {
    if (p.status === 'ok' || p.status === 'erro' || p.status === 'ja_processada') {
      return acc + (p.status === 'ok' ? p.totalRows : p.savedRows)
    }
    return acc + p.savedRows
  }, 0)
  const savePct =
    saveRowsTotal > 0
      ? Math.round((saveRowsDone / saveRowsTotal) * 100)
      : saveProgress.length === 0
        ? 0
        : Math.round((saveDoneFolders / saveProgress.length) * 100)

  const progressPct =
    processPhase === 'gravando'
      ? savePct
      : processPhase === 'lendo'
        ? readPct
        : saveProgress.length > 0
          ? 100
          : readPct

  const progressTitle =
    processPhase === 'gravando'
      ? 'Gravação no banco'
      : processPhase === 'lendo'
        ? 'Leitura de arquivos'
        : saveProgress.length > 0
          ? 'Processamento concluído'
          : 'Processamento'

  return (
    <div className="flex-1 p-6 space-y-6">
      {error && (
        <div className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg p-4 text-red-700 dark:text-red-400">
          {error}
        </div>
      )}

      <div className="bg-white dark:bg-gray-800 shadow rounded-lg border border-gray-200 dark:border-gray-700 p-6 space-y-4">
        <div className="flex flex-wrap items-center gap-3">
          <input
            ref={(el) => {
              folderInputRef.current = el
              if (el) {
                el.setAttribute('webkitdirectory', '')
                el.setAttribute('directory', '')
              }
            }}
            type="file"
            className="hidden"
            multiple
            onChange={handleFolderChange}
            disabled={isProcessing || isLoadingSubfolders}
          />
          <Button
            type="button"
            variant="outline"
            onClick={() => void handleSelectRootClick()}
            disabled={isProcessing || isListingRoot || isLoadingSubfolders}
          >
            {isListingRoot ? (
              <>
                <ArrowPathIcon className="h-5 w-5 animate-spin" />
                Lendo pastas...
              </>
            ) : (
              <>
                <FolderOpenIcon className="h-5 w-5" />
                Selecionar pasta raiz
              </>
            )}
          </Button>
          <Text size="sm" color="muted">
            Selecione sempre a pasta raiz. Em seguida escolha o ano e as subpastas desse ano.
          </Text>
        </div>

        {rootSession && (
          <div className="border border-gray-200 dark:border-gray-700 rounded-md overflow-hidden">
            <div className="px-4 py-3 bg-gray-50 dark:bg-gray-900/50 border-b border-gray-200 dark:border-gray-700 space-y-3">
              <div className="flex flex-wrap items-center justify-between gap-3">
                <div>
                  <span className="text-sm font-medium text-gray-700 dark:text-gray-300">
                    Pasta raiz: {rootSession.rootName}
                  </span>
                  <Text size="xs" color="muted" className="mt-0.5">
                    {rootSession.subfolders.length} subpasta
                    {rootSession.subfolders.length !== 1 ? 's' : ''} ·{' '}
                    {rootSession.years.length} ano
                    {rootSession.years.length !== 1 ? 's' : ''} identificado
                    {rootSession.years.length !== 1 ? 's' : ''}
                  </Text>
                </div>
                <Button
                  type="button"
                  size="sm"
                  variant="ghost"
                  onClick={cancelRootSession}
                  disabled={isLoadingSubfolders}
                >
                  Cancelar
                </Button>
              </div>

              <div className="max-w-xs">
                <label className="block text-xs font-medium text-gray-600 dark:text-gray-400 mb-1">
                  Ano para processar
                </label>
                <Select
                  value={selectedYear}
                  onChange={(e: React.ChangeEvent<HTMLSelectElement>) =>
                    onChangeYear(e.target.value)
                  }
                  disabled={isLoadingSubfolders || rootSession.years.length === 0}
                >
                  {rootSession.years.length === 0 ? (
                    <option value="">Nenhum ano encontrado</option>
                  ) : (
                    rootSession.years.map((y) => (
                      <option key={y} value={y}>
                        {y}
                      </option>
                    ))
                  )}
                </Select>
              </div>
            </div>

            {selectedYear && (
              <>
                <div className="flex flex-wrap items-center justify-between gap-3 px-4 py-2 border-b border-gray-200 dark:border-gray-700">
                  <label className="flex items-center gap-2 text-xs text-gray-700 dark:text-gray-300 cursor-pointer">
                    <input
                      type="checkbox"
                      checked={areAllYearSubfoldersSelected}
                      onChange={onToggleAllYearSubfolders}
                      disabled={isLoadingSubfolders || selectableSubfoldersForYear.length === 0}
                    />
                    <span>
                      Selecionar todas pendentes de {selectedYear} (
                      {selectableSubfoldersForYear.length})
                    </span>
                  </label>
                  <Button
                    type="button"
                    size="sm"
                    onClick={() => void handleAddSelectedSubfolders()}
                    disabled={
                      isLoadingSubfolders || selectedSubfolderNames.length === 0
                    }
                  >
                    {isLoadingSubfolders ? (
                      <>
                        <ArrowPathIcon className="h-4 w-4 animate-spin" />
                        Carregando...
                      </>
                    ) : (
                      `Adicionar (${selectedSubfolderNames.length})`
                    )}
                  </Button>
                </div>
                <ul
                  role="list"
                  className="divide-y divide-gray-200 dark:divide-gray-700 max-h-64 overflow-y-auto"
                >
                  {subfoldersForYear.length === 0 ? (
                    <li className="px-4 py-3 text-sm text-gray-500 dark:text-gray-400">
                      Nenhuma subpasta para o ano {selectedYear}.
                    </li>
                  ) : (
                    subfoldersForYear.map((s) => {
                      const already = pastasProcessadasNomes.has(s.name)
                      return (
                        <li key={s.name} className="px-4 py-2.5">
                          <label
                            className={`flex items-center gap-3 text-sm ${
                              already
                                ? 'text-gray-400 dark:text-gray-500 cursor-not-allowed'
                                : 'text-gray-700 dark:text-gray-300 cursor-pointer'
                            }`}
                          >
                            <input
                              type="checkbox"
                              checked={
                                already ? false : selectedSubfolderNames.includes(s.name)
                              }
                              onChange={(e) =>
                                onToggleSubfolder(s.name, e.target.checked)
                              }
                              disabled={isLoadingSubfolders || already}
                            />
                            <span className="font-medium flex-1">{s.name}</span>
                            {already && (
                              <span className="text-xs text-amber-700 dark:text-amber-400">
                                já processada
                              </span>
                            )}
                          </label>
                        </li>
                      )
                    })
                  )}
                </ul>
              </>
            )}
          </div>
        )}

        {pastas.length > 0 && (
          <div className="border border-gray-200 dark:border-gray-700 rounded-md overflow-hidden">
            <div className="flex flex-wrap items-center justify-between gap-3 px-4 py-3 bg-gray-50 dark:bg-gray-900/50 border-b border-gray-200 dark:border-gray-700">
              <div>
                <span className="text-sm font-medium text-gray-700 dark:text-gray-300">
                  Pastas para processar
                </span>
                <Text size="xs" color="muted" className="mt-0.5">
                  {pastas.length} pasta{pastas.length !== 1 ? 's' : ''}, {totalFileCount} arquivo
                  {totalFileCount !== 1 ? 's' : ''} Excel/CSV
                </Text>
              </div>
              <div className="flex flex-wrap items-center gap-2">
                <Button
                  type="button"
                  size="sm"
                  variant="ghost"
                  onClick={clearPastas}
                  disabled={isProcessing}
                >
                  Limpar
                </Button>
                <Button
                  type="button"
                  size="sm"
                  onClick={() => void handleProcess()}
                  disabled={isProcessing || totalFileCount === 0}
                >
                  {isProcessing ? (
                    <>
                      <ArrowPathIcon className="h-4 w-4 animate-spin" />
                      Processando...
                    </>
                  ) : (
                    `Processar (${pastas.length} pasta${pastas.length !== 1 ? 's' : ''}, ${totalFileCount} arquivo${totalFileCount !== 1 ? 's' : ''})`
                  )}
                </Button>
              </div>
            </div>
            <ul role="list" className="divide-y divide-gray-200 dark:divide-gray-700 max-h-64 overflow-y-auto">
              {pastas.map((p) => {
                const already = pastasProcessadasNomes.has(p.name)
                return (
                <li
                  key={p.name}
                  className="px-4 py-2.5 flex items-center gap-3 text-sm text-gray-700 dark:text-gray-300"
                >
                  <span className="font-medium flex-1">{p.name}</span>
                  {already && (
                    <span className="text-xs text-amber-700 dark:text-amber-400">
                      já processada
                    </span>
                  )}
                  <span className="text-xs text-gray-500 dark:text-gray-400">
                    {p.fileCount === 0
                      ? 'sem Excel/CSV'
                      : `${p.fileCount} arquivo${p.fileCount !== 1 ? 's' : ''}`}
                  </span>
                  <button
                    type="button"
                    onClick={() => removePasta(p.name)}
                    disabled={isProcessing}
                    className="p-1 rounded text-gray-400 hover:text-red-600 dark:hover:text-red-400 disabled:opacity-50"
                    aria-label={`Remover ${p.name}`}
                  >
                    <XMarkIcon className="h-4 w-4" />
                  </button>
                </li>
                )
              })}
            </ul>
          </div>
        )}
      </div>

      {(isProcessing || fileProgress.length > 0 || saveProgress.length > 0) && (
        <div className="bg-white dark:bg-gray-800 shadow rounded-lg border border-gray-200 dark:border-gray-700 p-6 space-y-4">
          <div className="flex items-center justify-between gap-4">
            <Text size="sm" className="text-gray-700 dark:text-gray-300 font-medium">
              {progressTitle}
            </Text>
            {isProcessing && <LoadingSpinner />}
          </div>
          <div className="w-full bg-gray-200 dark:bg-gray-700 rounded-full h-2.5">
            <div
              className="bg-primary-600 h-2.5 rounded-full transition-all duration-300"
              style={{ width: `${progressPct}%` }}
            />
          </div>

          {(processPhase === 'lendo' || (processPhase === 'idle' && saveProgress.length === 0)) && (
            <Text size="sm" color="muted">
              {doneCount} de {fileProgress.length} arquivo{fileProgress.length !== 1 ? 's' : ''}
              {currentFileLabel && processPhase === 'lendo' ? ` — ${currentFileLabel}` : ''}
            </Text>
          )}

          {(processPhase === 'gravando' ||
            (processPhase === 'idle' && saveProgress.length > 0)) && (
            <Text size="sm" color="muted">
              {saveRowsTotal > 0
                ? `${saveRowsDone} de ${saveRowsTotal} registro${saveRowsTotal !== 1 ? 's' : ''} gravado${saveRowsTotal !== 1 ? 's' : ''}`
                : `${saveDoneFolders} de ${saveProgress.length} pasta${saveProgress.length !== 1 ? 's' : ''}`}
              {currentFileLabel && processPhase === 'gravando' ? ` — ${currentFileLabel}` : ''}
              {processPhase === 'idle' && saveProgress.length > 0
                ? ` · ${saveDoneFolders} pasta${saveDoneFolders !== 1 ? 's' : ''} processada${saveDoneFolders !== 1 ? 's' : ''}`
                : ''}
            </Text>
          )}

          {fileProgress.length > 0 && processPhase !== 'gravando' && (
            <ul className="max-h-48 overflow-y-auto divide-y divide-gray-200 dark:divide-gray-700 border border-gray-200 dark:border-gray-700 rounded-md">
              {fileProgress.map((p) => (
                <li
                  key={p.id}
                  className="px-3 py-2 text-sm flex items-start gap-2 text-gray-700 dark:text-gray-300"
                >
                  {p.status === 'ok' && (
                    <CheckCircleIcon className="h-5 w-5 text-green-600 dark:text-green-400 shrink-0" />
                  )}
                  {p.status === 'erro' && (
                    <ExclamationTriangleIcon className="h-5 w-5 text-red-600 dark:text-red-400 shrink-0" />
                  )}
                  {(p.status === 'pendente' || p.status === 'lendo') && (
                    <ArrowPathIcon
                      className={`h-5 w-5 text-gray-400 shrink-0 ${p.status === 'lendo' ? 'animate-spin' : ''}`}
                    />
                  )}
                  <div className="min-w-0 flex-1">
                    <span className="font-medium">{p.pasta}/</span>
                    {p.fileName}
                    {p.status === 'ok' && (
                      <span className="text-xs text-gray-500 dark:text-gray-400 ml-2">
                        {p.rowCount} linha{p.rowCount !== 1 ? 's' : ''}
                      </span>
                    )}
                    {p.status === 'erro' && p.error && (
                      <span className="block text-xs text-red-600 dark:text-red-400 mt-0.5">
                        {p.error}
                      </span>
                    )}
                  </div>
                </li>
              ))}
            </ul>
          )}

          {saveProgress.length > 0 &&
            (processPhase === 'gravando' || processPhase === 'idle') && (
              <ul className="max-h-48 overflow-y-auto divide-y divide-gray-200 dark:divide-gray-700 border border-gray-200 dark:border-gray-700 rounded-md">
                {saveProgress.map((p) => (
                  <li
                    key={p.pasta}
                    className="px-3 py-2 text-sm flex items-start gap-2 text-gray-700 dark:text-gray-300"
                  >
                    {p.status === 'ok' && (
                      <CheckCircleIcon className="h-5 w-5 text-green-600 dark:text-green-400 shrink-0" />
                    )}
                    {(p.status === 'erro' || p.status === 'ja_processada') && (
                      <ExclamationTriangleIcon className="h-5 w-5 text-amber-600 dark:text-amber-400 shrink-0" />
                    )}
                    {(p.status === 'pendente' || p.status === 'gravando') && (
                      <ArrowPathIcon
                        className={`h-5 w-5 text-gray-400 shrink-0 ${p.status === 'gravando' ? 'animate-spin' : ''}`}
                      />
                    )}
                    <div className="min-w-0 flex-1">
                      <span className="font-medium">{p.pasta}</span>
                      <span className="text-xs text-gray-500 dark:text-gray-400 ml-2">
                        {p.savedRows}/{p.totalRows} registro{p.totalRows !== 1 ? 's' : ''}
                      </span>
                      {p.status === 'erro' && p.error && (
                        <span className="block text-xs text-red-600 dark:text-red-400 mt-0.5">
                          {p.error}
                        </span>
                      )}
                      {p.status === 'ja_processada' && p.error && (
                        <span className="block text-xs text-amber-700 dark:text-amber-400 mt-0.5">
                          {p.error}
                        </span>
                      )}
                    </div>
                  </li>
                ))}
              </ul>
            )}

          {processPhase === 'idle' &&
            saveProgress.length > 0 &&
            fileProgress.length > 0 && (
              <details className="text-sm text-gray-600 dark:text-gray-400">
                <summary className="cursor-pointer hover:text-gray-800 dark:hover:text-gray-200">
                  Detalhe da leitura de arquivos
                </summary>
                <ul className="mt-2 max-h-40 overflow-y-auto divide-y divide-gray-200 dark:divide-gray-700 border border-gray-200 dark:border-gray-700 rounded-md">
                  {fileProgress.map((p) => (
                    <li
                      key={`done-${p.id}`}
                      className="px-3 py-2 flex items-start gap-2 text-gray-700 dark:text-gray-300"
                    >
                      {p.status === 'ok' ? (
                        <CheckCircleIcon className="h-5 w-5 text-green-600 dark:text-green-400 shrink-0" />
                      ) : (
                        <ExclamationTriangleIcon className="h-5 w-5 text-red-600 dark:text-red-400 shrink-0" />
                      )}
                      <div className="min-w-0 flex-1">
                        <span className="font-medium">{p.pasta}/</span>
                        {p.fileName}
                        {p.status === 'ok' && (
                          <span className="text-xs text-gray-500 dark:text-gray-400 ml-2">
                            {p.rowCount} linha{p.rowCount !== 1 ? 's' : ''}
                          </span>
                        )}
                        {p.status === 'erro' && p.error && (
                          <span className="block text-xs text-red-600 dark:text-red-400 mt-0.5">
                            {p.error}
                          </span>
                        )}
                      </div>
                    </li>
                  ))}
                </ul>
              </details>
            )}
        </div>
      )}

      {rows.length > 0 && (
        <div className="bg-white dark:bg-gray-800 shadow rounded-lg border border-gray-200 dark:border-gray-700">
          <div className="px-6 pt-4 border-b border-gray-200 dark:border-gray-700">
            <div className="border-b border-gray-200 dark:border-gray-700 overflow-x-auto">
              <nav className="-mb-px flex space-x-6">
                <button
                  type="button"
                  onClick={() => {
                    setActiveFolderTab(ALL_FOLDERS_TAB)
                    setCurrentPage(1)
                  }}
                  className={subTabClass(activeFolderTab === ALL_FOLDERS_TAB)}
                >
                  Todas ({rows.length})
                </button>
                {processedFolders.map((name) => {
                  const count = rows.filter((r) => r.pasta === name).length
                  return (
                    <button
                      key={name}
                      type="button"
                      onClick={() => {
                        setActiveFolderTab(name)
                        setCurrentPage(1)
                      }}
                      className={subTabClass(activeFolderTab === name)}
                    >
                      {name} ({count})
                    </button>
                  )
                })}
              </nav>
            </div>
          </div>

          <div className="px-6 py-4 border-b border-gray-200 dark:border-gray-700">
            <Input
              value={search}
              onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                setSearch(e.target.value)
                setCurrentPage(1)
              }}
              placeholder="Buscar por corretor, apólice, estipulante ou arquivo..."
            />
          </div>

          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
              <thead className="bg-gray-50 dark:bg-gray-900/50 sticky top-0">
                <tr>
                  {COLUMN_DEFS.map((col) => (
                    <SortableTableHeader
                      key={col.key}
                      sortKey={col.key}
                      currentSort={sortBy}
                      sortOrder={sortOrder}
                      onSort={toggleSort}
                      compact
                    >
                      {col.label}
                    </SortableTableHeader>
                  ))}
                </tr>
              </thead>
              <tbody className="bg-white dark:bg-gray-800 divide-y divide-gray-200 dark:divide-gray-700">
                {pageRows.length === 0 ? (
                  <tr>
                    <td
                      colSpan={COLUMN_DEFS.length}
                      className="px-3 py-8 text-center text-sm text-gray-500 dark:text-gray-400"
                    >
                      Nenhum registro encontrado
                    </td>
                  </tr>
                ) : (
                  pageRows.map((row, idx) => (
                    <tr
                      key={`${row.pasta}-${row.arquivo}-${startIndex + idx}`}
                      className="hover:bg-gray-50 dark:hover:bg-gray-700/40"
                    >
                      {COLUMN_DEFS.map((col) => {
                        const raw = row[col.key]
                        const display = MONEY_KEYS.has(col.key)
                          ? formatMoneyBr(raw as number | null)
                          : raw === null || raw === undefined || raw === ''
                            ? '—'
                            : String(raw)
                        return (
                          <td
                            key={col.key}
                            className={`px-3 py-2 text-sm whitespace-nowrap text-gray-900 dark:text-gray-100 ${
                              MONEY_KEYS.has(col.key) ? 'tabular-nums text-right' : ''
                            }`}
                          >
                            {display}
                          </td>
                        )
                      })}
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>

          <div className="px-6 py-4 border-t border-gray-200 dark:border-gray-700">
            <div className="flex items-center justify-between">
              <Text size="sm" className="text-gray-700 dark:text-gray-300">
                {sortedRows.length === 0
                  ? 'Nenhum registro'
                  : `Mostrando ${sortedRows.length === 0 ? 0 : startIndex + 1} a ${endIndex} de ${sortedRows.length} registro${sortedRows.length !== 1 ? 's' : ''}`}
              </Text>

              {totalPages > 1 && (
                <div className="flex items-center space-x-2">
                  <button
                    type="button"
                    onClick={() => setCurrentPage((p) => Math.max(1, p - 1))}
                    disabled={currentPage === 1}
                    className="px-3 py-2 text-sm font-medium text-gray-500 bg-white border border-gray-300 rounded-md hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed dark:bg-gray-800 dark:border-gray-600 dark:text-gray-300 dark:hover:bg-gray-700"
                  >
                    Anterior
                  </button>
                  <div className="flex space-x-1">
                    {Array.from({ length: Math.min(5, totalPages) }, (_, i) => {
                      let pageNumber: number
                      if (totalPages <= 5) {
                        pageNumber = i + 1
                      } else if (currentPage <= 3) {
                        pageNumber = i + 1
                      } else if (currentPage >= totalPages - 2) {
                        pageNumber = totalPages - 4 + i
                      } else {
                        pageNumber = currentPage - 2 + i
                      }
                      return (
                        <button
                          key={pageNumber}
                          type="button"
                          onClick={() => setCurrentPage(pageNumber)}
                          className={`px-3 py-2 text-sm font-medium rounded-md ${
                            currentPage === pageNumber
                              ? 'bg-primary-600 text-white'
                              : 'text-gray-500 bg-white border border-gray-300 hover:bg-gray-50 dark:bg-gray-800 dark:border-gray-600 dark:text-gray-300 dark:hover:bg-gray-700'
                          }`}
                        >
                          {pageNumber}
                        </button>
                      )
                    })}
                  </div>
                  <button
                    type="button"
                    onClick={() => setCurrentPage((p) => Math.min(totalPages, p + 1))}
                    disabled={currentPage === totalPages}
                    className="px-3 py-2 text-sm font-medium text-gray-500 bg-white border border-gray-300 rounded-md hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed dark:bg-gray-800 dark:border-gray-600 dark:text-gray-300 dark:hover:bg-gray-700"
                  >
                    Próxima
                  </button>
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  )
}

export default ComissoesTab
